import {
    BuildingOfficeIcon,
    CheckBadgeIcon,
    ClockIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { animations, viewportConfig } from '../../config/animations';

const WhyChoose = () => {
  const { t } = useTranslation();

  const differentials = [
    {
      icon: CheckBadgeIcon,
      title: t('whyChoose.differentials.oil.title'),
      description: t('whyChoose.differentials.oil.description'),
      color: 'from-avoprime-light to-green-400',
    },
    {
      icon: SparklesIcon,
      title: t('whyChoose.differentials.dryMatter.title'),
      description: t('whyChoose.differentials.dryMatter.description'),
      color: 'from-avoprime-secondary to-avoprime-primary',
    },
    {
      icon: ClockIcon,
      title: t('whyChoose.differentials.durability.title'),
      description: t('whyChoose.differentials.durability.description'),
      color: 'from-avoprime-primary to-avoprime-secondary',
    },
  ];


  return (
    <section className='py-20 bg-gradient-to-br from-avoprime-primary/5 to-avoprime-secondary/5'>
      <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
        <motion.div
          {...animations.fadeInUp}
          whileInView={animations.fadeInUp.animate}
          viewport={viewportConfig}
          className='text-center mb-16'
        >
          <h2 className='text-4xl sm:text-5xl lg:text-6xl font-eras font-bold text-avoprime-primary mb-6'>
            {t('whyChoose.title')}
          </h2>
          <p className='text-xl lg:text-2xl font-montserrat text-gray-700 max-w-4xl mx-auto leading-relaxed'>
            {t('whyChoose.subtitle')}
          </p>
        </motion.div>

        {/* Main Feature */}
        <motion.div
          {...animations.fadeInUpDelayed}
          whileInView={animations.fadeInUpDelayed.animate}
          viewport={viewportConfig}
          className='bg-gradient-to-r from-avoprime-primary to-avoprime-secondary rounded-3xl p-8 lg:p-12 text-white mb-16'
        >
          <div className='grid lg:grid-cols-2 gap-8 items-center'>
            <div>
              <h3 className='text-3xl lg:text-4xl font-eras font-bold mb-6'>
                {t('whyChoose.mainFeature.title')}
              </h3>
              <p className='text-lg lg:text-xl font-montserrat opacity-90 mb-6 leading-relaxed'>
                {t('whyChoose.mainFeature.description')}
              </p>
              <div className='flex items-center space-x-4'>
                <div className='w-12 h-12 bg-white/20 rounded-full flex items-center justify-center'>
                  <BuildingOfficeIcon className='w-6 h-6 text-white' />
                </div>
                <div>
                  <div className='font-eras font-bold text-lg'>
                    {t('whyChoose.mainFeature.altitude')}
                  </div>
                  <div className='text-sm opacity-80'>
                    {t('whyChoose.mainFeature.altitudeDesc')}
                  </div>
                </div>
              </div>
            </div>
            <div className='relative'>
              <div
                className='w-full h-64 lg:h-80 rounded-2xl bg-cover bg-center'
                style={{
                  backgroundImage: "url('/terroir-secondary.webp')",
                }}
              />
              <div className='absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-2xl' />
            </div>
          </div>
        </motion.div>

        {/* Differentials Grid */}
        <motion.div
          variants={animations.containerVariants}
          initial='hidden'
          whileInView='visible'
          viewport={viewportConfig}
          className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
        >
          {differentials.map((differential, index) => (
            <motion.div
              key={index}
              variants={animations.itemVariants}
              className='group relative'
            >
              <div className='h-64 bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 flex flex-col'>
                <div
                  className={`w-16 h-16 bg-gradient-to-br ${differential.color} rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300`}
                >
                  <differential.icon className='w-8 h-8 text-white' />
                </div>
                <h3 className='text-xl font-eras font-bold text-avoprime-primary mb-4 text-center'>
                  {differential.title}
                </h3>
                <p className='text-gray-600 font-montserrat text-center leading-relaxed flex-1 flex items-center justify-center'>
                  {differential.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default WhyChoose;
